import { useEffect, useMemo, useRef, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
} from "recharts";

interface LabelConfig {
  column_index: number;
  color?: string;
  multiplier?: number;
  offset?: number;
  label?: string;
}

interface DeviceLabel {
  column_index: number;
  label: string;
}

interface StandardChartComponentProps {
  config: {
    device_id: number;
    chart_labels: LabelConfig[];
  };
  data: {
    timestamp: string;
    values: Record<string, number | null> | any;
  }[];
  deviceLabels: DeviceLabel[];

  onZoomRange: (start: string, end: string) => void;
  onResetZoom: () => void;
  isZoomed: boolean;
}

const formatChartValue = (value: number | null | undefined): string => {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return "--";
  }

  return Number(value.toFixed(3)).toString();
};

// const clamp = (v: number, min: number, max: number) =>
//   Math.min(Math.max(v, min), max);

const SELECTION_COLOR = "#3b82f6";

export default function StandardChartComponent({
  config,
  data,
  deviceLabels,
  onZoomRange,
  onResetZoom,
  isZoomed,
}: StandardChartComponentProps) {
  const getLabelName = (column_index: number): string => {
    const fromConfig = config.chart_labels?.find(
      (l) => l.column_index === column_index
    ) as LabelConfig | undefined;

    if (fromConfig?.label) return fromConfig.label;

    return (
      deviceLabels.find((l) => l.column_index === column_index)?.label ||
      `Col ${column_index}`
    );
  };

  const series = useMemo(() => {
    const fallback = ["#00ffff", "#00ff00", "#ffff00", "#ff0000", "#ff00ff", "#ffa500"];
    return (config.chart_labels || []).map((l, i) => {
      const name = getLabelName(l.column_index);
      return {
        column_index: l.column_index,
        name,
        stroke: l.color || fallback[i % fallback.length],
        multiplier: l.multiplier ?? 1,
        offset: l.offset ?? 0,
      };
    });
  }, [config.chart_labels, deviceLabels]);

  const [hiddenKeys, setHiddenKeys] = useState<Set<string>>(() => new Set());

  const toggleKey = (key: string) => {
    setHiddenKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const getRawValue = (entry: any, columnIndex: number) => {
    if (!entry?.values) return null;

    if (Array.isArray(entry.values)) {
      return entry.values[columnIndex] ?? null;
    }

    return (
      entry.values?.[String(columnIndex)] ??
      entry.values?.[columnIndex] ??
      null
    );
  };

  const isStatSeries = (name: string) => /(AVG|MIN|MAX)/i.test(name);

  // Full, un-zoomed chart data.
  const chartData = useMemo(() => {
    return data.map((entry) => {
      const row: Record<string, any> = { time: entry.timestamp };

      for (const s of series) {
        const rawVal = getRawValue(entry, s.column_index);

        row[s.name] =
          rawVal === undefined || rawVal === null
            ? null
            : Math.round((rawVal * s.multiplier + s.offset) * 1000) / 1000;
      }

      return row;
    });
  }, [data, series]);

  // ---- Click-and-drag zoom state -----------------------------------------
  // const fullLength = chartData.length;

  // // zoomStart / zoomEnd are indices into the FULL chartData array, defining
  // // the currently visible window. null means "not zoomed" (show everything).
  // const [zoomStart, setZoomStart] = useState<number | null>(null);
  // const [zoomEnd, setZoomEnd] = useState<number | null>(null);

  // const visStart = zoomStart !== null ? clamp(zoomStart, 0, fullLength - 2) : 0;
  // const visEnd =
  //   zoomEnd !== null ? clamp(zoomEnd, visStart + 1, fullLength - 1) : fullLength - 1;

  // const isZoomed = zoomStart !== null || zoomEnd !== null;

  // const zoomedChartData = useMemo(
  //   () => chartData.slice(visStart, visEnd + 1),
  //   [chartData, visStart, visEnd]
  // );

  // const resetZoom = () => {
  //   setZoomStart(null);
  //   setZoomEnd(null);
  // };
  const canZoom = chartData.length > 1;
  
  // Indices (local to the currently visible window / displayData array) of
  // the in-progress drag selection.
  const [selectLeftIndex, setSelectLeftIndex] = useState<number | null>(null);
  const [selectRightIndex, setSelectRightIndex] = useState<number | null>(null);

  const selectLeftRef = useRef<number | null>(null);
  const selectRightRef = useRef<number | null>(null);
  useEffect(() => {
    selectLeftRef.current = selectLeftIndex;
  }, [selectLeftIndex]);
  useEffect(() => {
    selectRightRef.current = selectRightIndex;
  }, [selectRightIndex]);

  const isSelecting = selectLeftIndex !== null;

  const handleChartMouseDown = (e: any) => {
    if (!e || e.activeTooltipIndex === undefined || e.activeTooltipIndex === null) return;
    setSelectLeftIndex(e.activeTooltipIndex);
    setSelectRightIndex(e.activeTooltipIndex);
  };

  const handleChartMouseMove = (e: any) => {
    if (selectLeftIndex === null) return;
    if (!e || e.activeTooltipIndex === undefined || e.activeTooltipIndex === null) return;
    setSelectRightIndex(e.activeTooltipIndex);
  };

  // Finalize the selection on mouseup anywhere on the page (not just inside
  // the chart), so a drag that ends outside the SVG still zooms.
  // useEffect(() => {
  //   if (selectLeftIndex === null) return;

  //   const onWindowUp = () => {
  //     const left = selectLeftRef.current;
  //     const right = selectRightRef.current;

  //     if (left !== null && right !== null && left !== right) {
  //       const lo = Math.min(left, right);
  //       const hi = Math.max(left, right);
  //       setZoomStart(visStart + lo);
  //       setZoomEnd(visStart + hi);
  //     }

  //     setSelectLeftIndex(null);
  //     setSelectRightIndex(null);
  //   };

  //   window.addEventListener("mouseup", onWindowUp);
  //   return () => window.removeEventListener("mouseup", onWindowUp);
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [selectLeftIndex, visStart]);
  useEffect(() => {
    if (selectLeftIndex === null) return;

    const onWindowUp = () => {
      const left = selectLeftRef.current;
      const right = selectRightRef.current;

      if (left !== null && right !== null && left !== right) {
        const lo = Math.min(left, right);
        const hi = Math.max(left, right);

        const start = chartData[lo]?.time;
        const end = chartData[hi]?.time;

        if (start && end) {
          onZoomRange(start, end);
        }
      }

      setSelectLeftIndex(null);
      setSelectRightIndex(null);
    };

    window.addEventListener("mouseup", onWindowUp);

    return () => {
      window.removeEventListener("mouseup", onWindowUp);
    };
  }, [selectLeftIndex, chartData, onZoomRange]);

  // For single-point stat series (e.g. AVG24/MAX24/MIN24), copy the value to
  // the first and last visible rows so connectNulls draws a full horizontal
  // line across whatever window is currently zoomed in on.
  // const displayData = useMemo(() => {
  //   const statNames = series.filter((s) => isStatSeries(s.name)).map((s) => s.name);
  //   if (statNames.length === 0) return zoomedChartData;
  //   if (zoomedChartData.length < 2) return zoomedChartData;

  //   const singlePoint: Record<string, number> = {};
  //   for (const name of statNames) {
  //     const nonNull = zoomedChartData.filter(
  //       (r) => r[name] !== null && r[name] !== undefined
  //     );
  //     if (nonNull.length === 1) {
  //       singlePoint[name] = nonNull[0][name];
  //     }
  //   }

  //   if (Object.keys(singlePoint).length === 0) return zoomedChartData;

  //   const result = zoomedChartData.map((row) => ({ ...row }));
  //   for (const [name, val] of Object.entries(singlePoint)) {
  //     for (const row of result) {
  //       if (row[name] !== null && row[name] !== undefined) {
  //         row[name] = null;
  //       }
  //     }
  //     result[0][name] = val;
  //     result[result.length - 1][name] = val;
  //   }
  //   return result;
  // }, [zoomedChartData, series]);
  const displayData = useMemo(() => {
    const statNames = series
      .filter((s) => isStatSeries(s.name))
      .map((s) => s.name);

    if (statNames.length === 0) return chartData;
    if (chartData.length < 2) return chartData;

    const singlePoint: Record<string, number> = {};

    for (const name of statNames) {
      const nonNull = chartData.filter(
        (r) => r[name] !== null && r[name] !== undefined
      );

      if (nonNull.length === 1) {
        singlePoint[name] = nonNull[0][name];
      }
    }

    if (Object.keys(singlePoint).length === 0) return chartData;

    const result = chartData.map((row) => ({ ...row }));

    for (const [name, val] of Object.entries(singlePoint)) {
      for (const row of result) {
        if (row[name] !== null && row[name] !== undefined) {
          row[name] = null;
        }
      }

      result[0][name] = val;
      result[result.length - 1][name] = val;
    }

    return result;
  }, [chartData, series]);

  const visibleSeriesKeys = useMemo(
    () => series.filter((s) => !hiddenKeys.has(s.name)).map((s) => s.name),
    [series, hiddenKeys]
  );

  let yMin = Number.POSITIVE_INFINITY;
  let yMax = Number.NEGATIVE_INFINITY;

  for (const row of displayData) {
    for (const k of visibleSeriesKeys) {
      const v = row[k];
      if (Number.isFinite(v)) {
        if (v < yMin) yMin = v;
        if (v > yMax) yMax = v;
      }
    }
  }

  const range = yMax - yMin;
  const pad = range !== 0 ? Math.abs(range) * 0.05 : (Math.abs(yMin) || 1) * 0.05;

  const yDomain: [number, number] =
    Number.isFinite(yMin) && Number.isFinite(yMax) ? [yMin - pad, yMax + pad] : [0, 1];

  const renderLegend = (props: any) => {
    const payload = props?.payload || [];
    return (
      <div
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        {payload.map((item: any) => {
          const key = item.value;
          const isHidden = hiddenKeys.has(key);

          return (
            <span
              key={key}
              onClick={() => toggleKey(key)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                cursor: "pointer",
                userSelect: "none",
                opacity: isHidden ? 0.4 : 1,
              }}
              title={isHidden ? "Click to show" : "Click to hide"}
            >
              <span
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: 2,
                  background: item.color,
                  border: "1px solid rgba(0,0,0,0.2)",
                }}
              />
              <span style={{ color: "#000000" }}>{key}</span>
            </span>
          );
        })}
      </div>
    );
  };

  // Resolve the current selection's local indices to actual time values so
  // ReferenceArea / ReferenceLine (which key off the "time" dataKey) line up
  // exactly with the plotted data, pixel-perfect, using Recharts' own scale.
  const selectionLoIndex =
    selectLeftIndex !== null && selectRightIndex !== null
      ? Math.min(selectLeftIndex, selectRightIndex)
      : null;
  const selectionHiIndex =
    selectLeftIndex !== null && selectRightIndex !== null
      ? Math.max(selectLeftIndex, selectRightIndex)
      : null;

  const showSelection =
    selectionLoIndex !== null &&
    selectionHiIndex !== null &&
    selectionLoIndex !== selectionHiIndex;

  const selectionX1 = showSelection ? displayData[selectionLoIndex as number]?.time : undefined;
  const selectionX2 = showSelection ? displayData[selectionHiIndex as number]?.time : undefined;

  // const canZoom = fullLength > 1;

  return (
    <div
      style={{
        backgroundColor: "#ffffff",
        borderRadius: "4px",
        padding: "2px",
        width: "100%",
        height: "100%",
        position: "relative",
        userSelect: isSelecting ? "none" : "auto",
      }}
    >
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={displayData}
          onMouseDown={canZoom ? handleChartMouseDown : undefined}
          onMouseMove={canZoom ? handleChartMouseMove : undefined}
          style={{ cursor: canZoom ? "crosshair" : "default" }}
        >
          <CartesianGrid stroke="#ccc" strokeDasharray="3 3" />

          <XAxis
            dataKey="time"
            stroke="#000000"
            tick={{ fontSize: 10 }}
            interval="preserveStartEnd"
            minTickGap={20}
          />

          <YAxis
            stroke="#000000"
            domain={yDomain}
            tickFormatter={(value) => formatChartValue(value)}
          />

          <Tooltip
            content={({ label, payload }) => {
              if (isSelecting) return null;
              if (!payload || payload.length === 0) return null;

              const valid = payload.filter(
                (p) => p.value !== null && p.value !== undefined
              );

              if (valid.length === 0) return null;

              return (
                <div
                  style={{
                    backgroundColor: "#ffffff",
                    border: "1px solid #ccc",
                    borderRadius: 4,
                    padding: "6px 10px",
                    fontSize: 12,
                  }}
                >
                  <div
                    style={{
                      color: "#000",
                      marginBottom: 4,
                      fontWeight: 600,
                    }}
                  >
                    {label}
                  </div>

                  {valid.map((p) => (
                    <div key={p.dataKey} style={{ color: p.color }}>
                      {p.name} : {formatChartValue(Number(p.value))}
                    </div>
                  ))}
                </div>
              );
            }}
          />

          <Legend
            verticalAlign="top"
            align="center"
            height={36}
            wrapperStyle={{ marginBottom: "-10px" }}
            payload={series.map((s) => ({
              value: s.name,
              id: s.name,
              color: s.stroke,
              type: isStatSeries(s.name) ? "circle" : "line",
            }))}
            content={renderLegend}
          />

          {series.map((s) => {
            if (hiddenKeys.has(s.name)) return null;

            const isStat = isStatSeries(s.name);

            return (
              <Line
                key={s.name}
                type="linear"
                dataKey={s.name}
                stroke={s.stroke}
                strokeWidth={isStat ? 2 : 2}
                strokeOpacity={1}
                dot={isStat ? { r: 4, strokeWidth: 2 } : false}
                activeDot={isStat ? { r: 6 } : { r: 4 }}
                connectNulls={true}
                isAnimationActive={false}
              />
            );
          })}

          {/* highlighted region + guidelines while the user is dragging a selection */}
          {showSelection && (
            <>
              <ReferenceArea
                x1={selectionX1}
                x2={selectionX2}
                strokeOpacity={0.5}
                stroke={SELECTION_COLOR}
                fill={SELECTION_COLOR}
                fillOpacity={0.15}
                ifOverflow="extendDomain"
              />
              <ReferenceLine x={selectionX1} stroke={SELECTION_COLOR} strokeDasharray="4 3" />
              <ReferenceLine x={selectionX2} stroke={SELECTION_COLOR} strokeDasharray="4 3" />
            </>
          )}
        </LineChart>
      </ResponsiveContainer>

      {isZoomed && (
        <button
          // onClick={resetZoom}
          onClick={onResetZoom}
          style={{
            position: "absolute",
            top: 6,
            right: 6,
            zIndex: 5,
            fontSize: 11,
            padding: "4px 8px",
            background: "#111827",
            color: "#fff",
            border: "none",
            borderRadius: 4,
            cursor: "pointer",
            opacity: 0.85,
          }}
        >
          Reset zoom
        </button>
      )}
    </div>
  );
}